/**
 * Tipos del tracker «¿Dónde está Papá Noel?» (`/donde-esta-papa-noel`).
 *
 * El tracker es una **experiencia navideña simulada**, no un seguimiento real:
 * todo lo que muestra se deriva de la hora actual con funciones puras y
 * deterministas (ver `schedule.ts` y `stats.ts`). Nada de aleatoriedad en
 * render — servidor y cliente tienen que coincidir byte a byte en el primer
 * pintado, y además una cifra que salta al azar en cada tick no se lee como
 * "mágica" sino como rota.
 */

import type { PublicLocale } from '@/lib/locale-prefix'
import { TRACKER_PUBLISHED } from '@/lib/locale-capabilities'

/** Locales con página pública; la misma lista gobierna rutas y contenido.
 *  Fuente única: el registro de capacidades (`lib/locale-capabilities`).
 *  Puede haber mercado soportado sin tracker publicado; la decisión de
 *  publicación se toma ahí, no aquí. */
export const TRACKER_LOCALES = TRACKER_PUBLISHED

export type TrackerLocale = (typeof TRACKER_LOCALES)[number]

/**
 * Tramos del recorrido, en el orden en que Papá Noel los cruza.
 *
 * El orden lo manda el huso horario (de UTC+13 a UTC−10), que es como avanza
 * de verdad la Nochebuena por el planeta. Por eso África aparece **antes** que
 * Europa occidental: El Cairo y Johannesburgo están en UTC+2 y Madrid en UTC+1.
 */
export type RouteLegKey =
  | 'north-pole'
  | 'pacific'
  | 'oceania'
  | 'east-asia'
  | 'southeast-asia'
  | 'central-asia'
  | 'middle-east'
  | 'eastern-europe'
  | 'africa'
  | 'western-europe'
  | 'south-america'
  | 'north-america'
  | 'north-pole-return'

/** Una parada del itinerario. El nombre visible vive en el contenido por idioma. */
export interface RouteStop {
  /** Clave estable; es también la clave del nombre localizado. */
  id: string
  leg: RouteLegKey
  /**
   * Desfase UTC en horas **en diciembre** (incluye el horario de verano
   * austral: Sídney +11, Auckland +13). Fija la hora a la que pasa el trineo.
   */
  utcOffset: number
  /** Longitud en grados (−180..180). */
  lon: number
  /** Latitud en grados (−90..90). */
  lat: number
  /** Países que el tramo da por cubiertos al pasar por aquí (cifra mágica). */
  countries: number
}

/** En qué momento del año navideño estamos. */
export type TrackerPhase =
  /** La mayor parte del año: el taller prepara la Navidad. */
  | 'preparing'
  /** Las horas previas al despegue. */
  | 'liftoff'
  /** Nochebuena: el trineo está dando la vuelta al mundo. */
  | 'traveling'
  /** Misión cumplida, de vuelta en el Polo Norte. */
  | 'completed'

/** Instantes clave de una campaña navideña concreta, en epoch ms UTC. */
export interface TrackerSeason {
  /** Año de la Nochebuena a la que se refiere la temporada. */
  year: number
  /** Empiezan los preparativos finales (state 2). */
  liftoffStartMs: number
  /** Despegue del Polo Norte. */
  tripStartMs: number
  /** Última entrega del recorrido. */
  lastDeliveryMs: number
  /** Vuelta a casa: fin del recorrido. */
  tripEndMs: number
  /** Hasta cuándo se muestra el estado «misión cumplida». */
  completedUntilMs: number
}

/** Franja aproximada de paso por la zona del visitante, en epoch ms UTC. */
export interface VisitorWindow {
  startMs: number
  endMs: number
  /** True si la franja ya ha terminado. */
  passed: boolean
}

/** Posición geográfica del trineo. */
export interface SleighPosition {
  lon: number
  lat: number
}

/**
 * Todo lo que la interfaz necesita saber en un instante dado. Es el resultado
 * de una función pura de (`nowMs`, desfase horario del visitante), así que el
 * servidor puede calcularlo para el HTML indexable y el cliente recalcularlo
 * cada segundo sin discrepancias de hidratación.
 */
export interface TrackerState {
  nowMs: number
  phase: TrackerPhase
  season: TrackerSeason
  /** Avance del recorrido, 0..1 (0 antes de despegar, 1 al terminar). */
  progress: number
  /** Índice de la última parada alcanzada; −1 si aún no ha despegado. */
  stopIndex: number
  /** Paradas ya visitadas (incluye el Polo Norte de salida). */
  visitedCount: number
  /** Id de la última parada visitada (null antes del despegue). */
  lastStopId: string | null
  /** Id del próximo destino (null cuando ya ha vuelto a casa). */
  nextStopId: string | null
  /** Tramo en el que está el trineo ahora mismo. */
  currentLeg: RouteLegKey
  position: SleighPosition
  /** Objetivo de la cuenta atrás principal, en epoch ms UTC. */
  countdownTargetMs: number
  /** Milisegundos que faltan para ese objetivo (nunca negativo). */
  countdownMs: number
  /** Franja estimada de paso por la zona horaria del visitante. */
  visitorWindow: VisitorWindow
  /** Milisegundos hasta la Navidad (00:00 del 25) en la zona del visitante. */
  msToChristmas: number
  /** Países acumulados hasta la última parada alcanzada. */
  countriesVisited: number
}

/** Cifras mágicas que acompañan a cada fase. */
export interface MagicStats {
  giftsReady: number
  giftsDelivered: number
  lettersReceived: number
  elvesWorking: number
  reindeerTraining: number
  kilometers: number
  citiesVisited: number
  countriesVisited: number
  homesWaiting: number
  /** Duración del viaje en minutos (recorrido en curso o ya terminado). */
  travelMinutes: number
}
